import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { CamRequest } from '../../../asset-management/models/request/CamRequest';
import { map, delay, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { serviceUris } from '../../../../../environments/services';
import { PlantService } from '../../../../shared/services/plant.service';
import { SiteService } from '../../../../shared/services/site.service';
import {
  CreateWorkOrderRequest,
  CreateWorkOrderResponse,
  ValidationResponse,
  DropdownDataResponse,
  WorkOrderLine as WorkOrderLineInterface,
  ProcessCode,
  TireMake,
  TireSpecification,
  ProductionCustomer,
  Site,
  Plant,
  BarcodeValidationRequest,
  BarcodeValidationResponse,
  ValidateLineBarcodeRequest,
  ValidateLineBarcodeResponse,
  ValidateRtDotRequest,
  ValidateRtDotResponse,
  NextBarcodeRequest,
  NextBarcodeResponse,
  LengthOption,
  ApplicationOption,
  AxleOption,
  WheelOption,
  CasingGradeOption,
  SalesmanOption
} from './models/work-order.interfaces';
import { WorkOrderSettings } from './models/work-order-settings.model';

@Injectable({ providedIn: 'root' })
export class CreateWorkOrderService {
  private apiUrl = `${environment.apiUrl}/work-orders`;
  private http = inject(HttpClient);
  private plantService = inject(PlantService);
  private siteService = inject(SiteService);

  // ===== DROPDOWN DATA METHODS =====

  /**
   * Get work order initialization data including default status, sites, and plants
   * @param dealerId - The dealer ID from component context
   */
  getWorkOrderInitData(dealerId: string): Observable<{ defaultWorkOrderStatus: string; sites: Site[]; plants: Plant[] }> {
    console.log('CreateWorkOrderService: Loading init data for dealer:', dealerId);

    // Use dedicated services for sites and plants data
    const sitesCall = this.siteService.getSites(dealerId).pipe(
      tap((sites) => console.log('CreateWorkOrderService: Sites loaded:', sites.length)),
      catchError((error) => {
        console.error('CreateWorkOrderService: Failed to load sites:', error);
        throw error;
      })
    );

    const plantsCall = this.plantService.getPlants(dealerId).pipe(
      tap((plants) => console.log('CreateWorkOrderService: Plants loaded:', plants.length)),
      catchError((error) => {
        console.error('CreateWorkOrderService: Failed to load plants:', error);
        throw error;
      })
    );

    return forkJoin({
      sites: sitesCall,
      plants: plantsCall
    }).pipe(
      tap(() => console.log('CreateWorkOrderService: Both sites and plants loaded successfully')),
      map((responses) => {
        console.log('CreateWorkOrderService: Mapping responses to domain models');

        // Add label property for display: "code - name"
        const sites = responses.sites.map((site) => ({
          ...site,
          label: `${site.code} - ${site.name}`
        }));

        const plants = responses.plants.map((plant) => ({
          ...plant,
          label: `${plant.code} - ${plant.name}`
        }));

        console.log('CreateWorkOrderService: Domain models created - Sites:', sites.length, 'Plants:', plants.length);

        return {
          defaultWorkOrderStatus: '',
          sites: sites,
          plants: plants
        };
      }),
      catchError((error) => {
        console.error('CreateWorkOrderService: Error in getWorkOrderInitData:', error);
        // Fallback to mock data on error
        const mockSites = this.getMockSitesData();
        const mockPlants = this.getMockPlantsData();

        console.warn('CreateWorkOrderService: Using mock data as fallback');

        return of({
          defaultWorkOrderStatus: '',
          sites: mockSites,
          plants: mockPlants
        });
      })
    );
  }

  /**
   * Get all dropdown configuration data in a single call
   */
  getDropdownData(): Observable<DropdownDataResponse> {
    // TODO: Replace with actual API call
    // Return empty datasets instead of mock/sample data to avoid dummy values in UI
    return of({
      defaultWorkOrderStatus: '',
      sites: [],
      plants: [],
      processCodes: [],
      makes: [],
      series: [],
      sizes: [],
      treads: []
    } as DropdownDataResponse);
    // return this.http.get<DropdownDataResponse>(`${this.apiUrl}/dropdown-data`);
  }

  /**
   * Get process codes with UUID keys - SHARED by both bulk and individual forms
   * Calls backend: GET assets/dealers/{dealerId}/casings/getPlantProcessCode?plantId={plantId}
   * Legacy equivalent: WorkOrderHeaderManagedBean.getActiveProcessCodeList(plantId) — used for BULK entry only.
   * SC code is excluded from the bulk dropdown client-side via the bulkProcessCodes getter.
   *
   * @param dealerId - Required dealer identifier
   * @param plantId - Optional plant identifier for filtering.
   */
  getProcessCodes(dealerId: string, plantId?: string): Observable<ProcessCode[]> {
    // Call real backend API using serviceUris
    let url = `${environment.backOfficeUrl}${serviceUris.getPlantProcessCode.replace('{dealerId}', dealerId)}`;

    // Add plantId as query parameter if provided (legacy: passes plantId to getActiveProcessCodeList)
    if (plantId && plantId.trim() !== '') {
      url += `?plantId=${plantId}`;
    }

    return this.http
      .get<any>(url, {
        headers: { dealer: dealerId }
      })
      .pipe(
        map((response) => {
          // Backend returns ApiResponse<List<String>> - extract data array
          const processCodeStrings = response.data || [];
          // Convert strings to ProcessCode objects with UUID-like IDs
          return processCodeStrings.map((code: string, index: number) => ({
            id: `process-${code}`, // Composite key: process-RT01, process-RT02, etc.
            code: code,
            name: code,
            label: code,
            description: '',
            isActive: true,
            sortOrder: index + 1
          }));
        }),
        catchError((error) => {
          console.error('Error loading process codes:', error);
          // Fallback to mock data on error
          return of(this.getMockProcessCodesData());
        })
      );
  }

  /**
   * Fetch derived work type for a given process code from backend.
   * Returns an object with `workType` property or { workType: null } on error/unknown.
   */
  getWorkType(dealerId: string, processCode: string): Observable<{ workType: string | null }> {
    if (!processCode || !dealerId) return of({ workType: null });
    const url = `${environment.backOfficeUrl}${serviceUris.deriveWorkType.replace('{dealerId}', dealerId)}?processCode=${encodeURIComponent(
      processCode
    )}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const data = response?.data || null;
        return { workType: data?.workType ?? null } as { workType: string | null };
      }),
      catchError((err) => {
        console.error('Failed to fetch derived work type:', err);
        return of({ workType: null });
      })
    );
  }

  /**
   * Get process codes for the SINGLE-LINE entry form, applying customer-based CC/CP filtering.
   * Calls backend: GET assets/dealers/{dealerId}/casings/getLineProcessCode
   * Legacy equivalent: WorkOrderLineProcess.getActiveProcessCodeList() —
   *   CC is included only when the production customer has casingCreditFk set;
   *   CP is included only when casingPurchaseFk is set.
   *
   * @param dealerId  - Required dealer identifier
   * @param plantId   - Optional plant identifier for filtering
   * @param customerId - Optional production customer UUID for CC/CP filtering
   */
  getLineProcessCodes(dealerId: string, plantId?: string, customerId?: string): Observable<ProcessCode[]> {
    let url = `${environment.backOfficeUrl}${serviceUris.getLineProcessCode.replace('{dealerId}', dealerId)}`;

    const params: string[] = [];
    if (plantId && plantId.trim() !== '') {
      params.push(`plantId=${plantId}`);
    }
    if (customerId && customerId.trim() !== '') {
      params.push(`customerId=${customerId}`);
    }
    if (params.length) {
      url += '?' + params.join('&');
    }

    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const processCodeStrings = response.data || [];
        return processCodeStrings.map((code: string, index: number) => ({
          id: `process-${code}`,
          code: code,
          name: code,
          label: code,
          description: '',
          isActive: true,
          sortOrder: index + 1
        }));
      }),
      catchError((error) => {
        console.error('Error loading line process codes:', error);
        return of(this.getMockProcessCodesData());
      })
    );
  }

  /**
   * Get tire makes with UUID keys - SHARED by both bulk and individual forms
   * Calls backend: GET /v1/product-data/dropdown/makes
   * Legacy equivalent: WorkOrderHeaderManagedBean.getCasingMakeList()
   */
  getMakes(dealerId: string): Observable<TireMake[]> {
    // Call real backend API using serviceUris
    const url = `${environment.backOfficeUrl}${serviceUris.getValidMakesForDealer}`;
    return this.http
      .get<any>(url, {
        headers: { dealer: dealerId }
      })
      .pipe(
        map((response) => {
          // Backend returns ApiResponse<List<CasingAutoCompleteDTO>>
          const makesData = response.data || [];
          // Map CasingAutoCompleteDTO to TireMake interface
          return makesData.map((item: any, index: number) => ({
            id: item.uuid || item.id,
            code: item.code,
            name: item.description,
            label: `${item.code} - ${item.description}`,
            isActive: true,
            isValid: item.valid,
            sortOrder: index + 1
          }));
        }),
        catchError((error) => {
          console.error('Error loading tire makes:', error);
          return of(this.getMockTireMakesData());
        })
      );
  }

  /**
   * Get tire series with UUID keys - SHARED, optionally filtered by make
   * Calls backend: GET productData/dropdown/series?makeId={makeId}
   * Legacy equivalent: WorkOrderHeaderManagedBean.getCasingSeriesList(prefix, makeId)
   * Uses CASING_DEFINITION table to filter series by make
   */
  getSeries(dealerId: string, makeId?: string): Observable<TireSpecification[]> {
    // Use single endpoint with query parameters (matches legacy behavior)
    let url = `${environment.backOfficeUrl}${serviceUris.getCasingSeriesForDealer}`;

    // Add makeId as query parameter if provided (legacy: filters by make using CASING_DEFINITION)
    if (makeId) {
      url += `?makeId=${makeId}`;
    }

    return this.http
      .get<any>(url, {
        headers: { dealer: dealerId }
      })
      .pipe(
        map((response) => {
          // Handle both ApiResponse wrapper and direct array response
          const seriesData = response.data || response || [];
          return seriesData.map((item: any, index: number) => ({
            id: item.id || item.uuid,
            category: 'series' as const,
            value: item.code,
            description: item.description,
            label: `${item.code}`,
            makeId: makeId,
            isActive: true,
            sortOrder: index + 1
          }));
        }),
        catchError((error) => {
          console.error('Error loading tire series:', error);
          return of(this.getMockTireSpecificationsData());
        })
      );
  }

  /**
   * Get tire sizes with UUID keys - SHARED, optionally filtered by make/series and plant
   * Calls backend: GET productData/dropdown/sizes?makeId={makeId}&seriesId={seriesId}&plantId={plantId}
   * Legacy equivalent: WorkOrderHeaderManagedBean.getCasingSizeList(prefix, makeId, seriesId, filterBias)
   * Uses CASING_DEFINITION table to filter sizes by make and series
   * Filters BIAS construction if plant.filterBiasSizes=Y
   */
  getSizes(dealerId: string, makeId?: string, seriesId?: string, plantId?: string): Observable<TireSpecification[]> {
    // Use single endpoint with query parameters (matches legacy behavior)
    let url = `${environment.backOfficeUrl}${serviceUris.getCasingSizesForDealer}`;

    // Build query parameters (legacy: filters by make+series using CASING_DEFINITION, applies plant filterBias)
    const params: string[] = [];
    if (makeId) params.push(`makeId=${makeId}`);
    if (seriesId) params.push(`seriesId=${seriesId}`);
    if (plantId) params.push(`plantId=${plantId}`);

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http
      .get<any>(url, {
        headers: { dealer: dealerId }
      })
      .pipe(
        map((response) => {
          // Handle both ApiResponse wrapper and direct array response
          const sizesData = response.data || response || [];
          return sizesData.map((item: any, index: number) => ({
            id: item.id || item.uuid,
            category: 'size' as const,
            value: item.code,
            description: item.description,
            label: `${item.code}`,
            makeId: makeId,
            seriesId: seriesId,
            plantId: plantId,
            isActive: true,
            sortOrder: index + 1
          }));
        }),
        catchError((error) => {
          console.error('Error loading tire sizes:', error);
          return of(this.getMockTireSpecificationsData());
        })
      );
  }

  /**
   * Get tire treads with UUID keys - filtered by size, plant, make, series and moldType.
   * Calls backend: GET productData/dropdown/filterTreads
   * Legacy equivalent: TreadDescProcess.getTreadDescList()
   * Preferred treads (if a spec is set on the customer) are returned first, sorted by name,
   * followed by non-preferred treads also sorted by name (GRA-2919 logic).
   */
  getTreads(
    dealerId: string,
    sizeId?: string,
    plantId?: string,
    preferredTreadSpecificationPk?: string,
    makeId?: string,
    seriesId?: string,
    moldType?: string
  ): Observable<TireSpecification[]> {
    // Build query parameters mirroring legacy getTreadDescList inputs
    const params: string[] = [];
    if (sizeId) params.push(`sizeId=${sizeId}`);
    if (plantId) params.push(`plantId=${plantId}`);
    if (makeId) params.push(`makeId=${makeId}`);
    if (seriesId) params.push(`seriesId=${seriesId}`);
    if (moldType) params.push(`moldType=${moldType}`);
    if (preferredTreadSpecificationPk) params.push(`preferredTreadSpecPk=${preferredTreadSpecificationPk}`);

    const queryParams = params.length > 0 ? `?${params.join('&')}` : '';
    const url = `${environment.backOfficeUrl}${serviceUris.getTreadsForDealerFilter}${queryParams}`;

    return this.http
      .get<any>(url, {
        headers: { dealer: dealerId }
      })
      .pipe(
        map((response) => {
          // Handle both ApiResponse wrapper and direct array response
          const treadsData = response.data || response || [];
          return treadsData.map((item: any, index: number) => ({
            id: item.id,
            category: 'tread' as const,
            value: item.treadDesc,
            description: item.description || item.treadDesc,
            label: item.description || item.treadDesc,
            sizeId: sizeId,
            plantId: plantId,
            applicationId: item.application,
            axleId: item.axle,
            isActive: true,
            sortOrder: index + 1
          }));
        }),
        catchError((error) => {
          console.error('Error loading tire treads:', error);
          return of(this.getMockTireSpecificationsData());
        })
      );
  }

  /**
   * Get tread lengths for the work order line dropdown.
   * Calls backend: GET productData/dropdown/treadLengths
   * Legacy equivalent: WorkOrderDetailManagedBean.getAllLengthList() → TreadLengthProcess.findAllByPrefix()
   * Only called when headerSettings.lengthUsed is true (ZONE_SETUP.TREAD_LENGTH != 'N').
   */
  getTreadLengths(dealerId: string): Observable<LengthOption[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getTreadLengthsForDealer}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const data = response.data || [];
        return data.map((item: any) => ({
          id: item.uuid || item.id,
          label: item.code
        }));
      }),
      catchError((error) => {
        console.error('Error loading tread lengths:', error);
        return of([]);
      })
    );
  }

  /**
   * Get application codes for the work order line dropdown.
   * Calls backend: GET productData/dropdown/applicationCodes
   * Legacy equivalent: WorkOrderDetailManagedBean.getAllApplicationCodes() → ReferenceInformation type 'APPLICATION'
   */
  getApplicationCodes(dealerId: string): Observable<ApplicationOption[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getApplicationCodesForDealer}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const data = response.data || [];
        return data.map((item: any) => ({
          id: item.id, // referenceValueId — what the backend save expects
          value: item.code,
          label: item.code, // referenceValueId used as display label
          yardApplication: item.yardApplication,
          description: item.description
        }));
      }),
      catchError((error) => {
        console.error('Error loading application codes:', error);
        return of([]);
      })
    );
  }

  /**
   * Get casing grade options for the Override Grade dropdown.
   * Calls backend: GET productData/getAllGrades
   * Legacy equivalent: WorkOrderDetailManagedBean.getAllCasingGradeValues()
   * → CasingGradeProcess.getAllCasingGradeValues(): queries GRA_CASING_GRADE filtered by dealerFk, ordered by casingGrade ASC.
   */
  getCasingGrades(dealerId: string): Observable<CasingGradeOption[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getAllGrades}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const grades = response?.data?.grades || [];
        return grades.map((item: any) => ({
          id: item.casingGradePk,
          value: item.casingGrade,
          label: item.casingGrade
        }));
      }),
      catchError((error) => {
        console.error('Error loading casing grades:', error);
        return of([]);
      })
    );
  }

  /**
   * Get axle codes for the work order line dropdown.
   * Calls backend: GET productData/dropdown/axleCodes
   * Legacy equivalent: WorkOrderDetailManagedBean.getAllAxleCodes() → ReferenceInformation type 'AXLE'
   */
  getAxleCodes(dealerId: string): Observable<AxleOption[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getAxleCodesForDealer}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const data = response.data || [];
        return data.map((item: any) => ({
          id: item.id, // referenceValueId — what the backend save expects
          value: item.code,
          label: item.code, // referenceValueId used as display label
          yardApplication: item.yardApplication,
          description: item.description
        }));
      }),
      catchError((error) => {
        console.error('Error loading axle codes:', error);
        return of([]);
      })
    );
  }

  /**
   * Get wheel codes for the work order line dropdown.
   * Calls backend: GET productData/dropdown/wheelCodes
   * Legacy equivalent: WorkOrderDetailManagedBean.getAllWheelCodes() → ReferenceInformation type 'WHEEL'
   */
  getWheelCodes(dealerId: string): Observable<WheelOption[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getWheelCodesForDealer}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const data = response.data || [];
        return data.map((item: any) => ({
          id: item.id,
          value: item.code,
          label: item.code, // referenceValueId used as display label
          description: item.description
        }));
      }),
      catchError((error) => {
        console.error('Error loading wheel codes:', error);
        return of([]);
      })
    );
  }

  /**
   * Get wheel colors for the work order line dropdown.
   * Calls backend: GET productData/dropdown/wheelColors
   * Legacy equivalent: WorkOrderDetailManagedBean.getAllWheelColors() → ReferenceInformation type 'COLOR'
   */
  getWheelColors(dealerId: string): Observable<WheelOption[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getWheelColorsForDealer}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const data = response.data || [];
        return data.map((item: any) => ({
          id: item.id,
          value: item.code,
          label: item.code, // referenceValueId used as display label
          description: item.description
        }));
      }),
      catchError((error) => {
        console.error('Error loading wheel colors:', error);
        return of([]);
      })
    );
  }

  /**
   * Get all active reject reasons for the work order line SC/NP code dropdown.
   * Calls backend: GET productData/dropdown/rejectReasons
   * Legacy equivalent: WorkOrderDetailManagedBean.getAllScrapCodes() / getAllNpCodes()
   *   → both delegate to getAllScrapNpCodes() → IRejectReason.findAllByReason(prefix).
   *   The same list is used for both the Scrap Code (SC process code) and NP Code (NP process code) dropdowns.
   */
  getRejectReasons(dealerId: string): Observable<{ id: string; code: string; label: string }[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getRejectReasonsForDropdown}`;
    return this.http.get<any>(url, { headers: { dealer: dealerId } }).pipe(
      map((response) => {
        const data: any[] = response.data || [];
        return data.map((item) => ({
          id: item.rejectReasonPk,
          code: item.rejectReason,
          label: item.rejectReason + ' - ' + (item.description || '')
        }));
      }),
      catchError((error) => {
        console.error('Error loading reject reasons:', error);
        return of([]);
      })
    );
  }

  /**
   * Calculate work order received and due dates based on site and plant
   */
  calculateWorkOrderDates(siteId: string, plant: Plant): Observable<{ receivedDate: string; dueDate: string; deliveryDay: string }> {
    // Implement business logic using actual plant settings
    // This mirrors the legacy WorkOrderHeaderManagedBean.ajaxPopulateDates() logic

    console.log(`Calculating dates for site: ${siteId}, plant:`, plant);

    const today = new Date();
    let receivedDate = new Date(today);
    let dueDate = new Date(today);

    // Business Logic 1: Calculate Received Date
    // Legacy: if (GRAFlagRetrievalProcess.getBooleanFlag(EGRASetupFlag.SET_RECEIVED_DT_TO_PRVS_DAY, plantFk))
    // Use actual plant.receiveDtPrev property
    if (plant.receiveDtPrev === 'Y') {
      receivedDate.setDate(receivedDate.getDate() - 1);
      console.log('Plant has receiveDtPrev=Y, setting received date to previous day');
    }

    // Business Logic 2: Calculate Due Date
    // Legacy: Use customer turnaround time or plant DUE_DT_ELAPSED_TIME
    // Use actual plant.dueDayElapsedTime property
    let turnaroundDays = 7; // Default
    if (plant.dueDayElapsedTime) {
      turnaroundDays = parseInt(plant.dueDayElapsedTime, 10);
      if (isNaN(turnaroundDays)) {
        console.warn('Invalid dueDayElapsedTime, using default 7 days:', plant.dueDayElapsedTime);
        turnaroundDays = 7;
      }
    }

    console.log(`Using turnaround days from plant.dueDayElapsedTime: ${turnaroundDays}`);

    // Add turnaround days to received date
    dueDate = new Date(receivedDate);
    dueDate.setDate(dueDate.getDate() + turnaroundDays);

    // Business Logic 3: Handle Weekend Adjustment (Legacy logic)
    // Legacy: if (GraDateUtils.getDay(dueDatel).equals("F")) dueDatel = GraDateUtils.addDays(dueDatel, 2);
    // Legacy: if (GraDateUtils.getDay(dueDatel).equals("G")) dueDatel = GraDateUtils.addDays(dueDatel, 1);
    // Only adjust if plant does NOT allow weekend due dates
    if (plant.allowDueDateOnWeekEnd !== 'Y') {
      console.log('Plant does not allow weekend due dates, checking for adjustment');
      dueDate = this.adjustDateForWeekend(dueDate);
    } else {
      console.log('Plant allows weekend due dates, no adjustment needed');
    }

    // Business Logic 4: Calculate Delivery Day (Legacy: GraDateUtils.getDay())
    // A=Monday, B=Tuesday, C=Wednesday, D=Thursday, E=Friday, F=Saturday, G=Sunday
    const deliveryDayMap = ['G', 'A', 'B', 'C', 'D', 'E', 'F'];
    const deliveryDay = deliveryDayMap[dueDate.getDay()];

    const response = {
      receivedDate: receivedDate.toISOString().split('T')[0], // YYYY-MM-DD format
      dueDate: dueDate.toISOString().split('T')[0], // YYYY-MM-DD format
      deliveryDay: deliveryDay
    };

    console.log('Calculated dates:', response);

    // Simulate API delay
    return of(response).pipe(delay(500));
  }

  /**
   * Adjust date to skip weekends (legacy: GraDateUtils weekend logic)
   * If Saturday (6), add 2 days to move to Monday
   * If Sunday (0), add 1 day to move to Monday
   */
  private adjustDateForWeekend(date: Date): Date {
    // Use local date components to avoid timezone issues
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = localDate.getDay();

    if (dayOfWeek === 6) {
      // Saturday (legacy: "F")
      date.setDate(date.getDate() + 2);
    } else if (dayOfWeek === 0) {
      // Sunday (legacy: "G")
      date.setDate(date.getDate() + 1);
    }

    return date;
  }

  /**
   * Get production customers based on header customer selection
   * Calls backend: GET /v1/customers/production?customerUuid={customerUuid} (TODO: Create this endpoint)
   * Legacy equivalent: WorkOrderHeaderManagedBean.getProdCustomerList()
   *
   * Business Logic (from legacy):
   * - If header customer is TYPE_CUSTOMER: return just that customer
   * - If header customer is TYPE_ASSOCIATE_DEALER: return all associated customers + the associate dealer
   */
  /**
   * Look up a single customer by exact customer code or CNPJ/fiscal ID
   * Returns the customer DTO if found, or null if not found
   */
  lookupCustomerByCode(
    dealerId: string,
    userLogin: string,
    plantId: string,
    searchCode: string,
    searchCnpj: boolean,
    activeOnly: boolean,
    customerTypes: string
  ): Observable<any | null> {
    const url = `${environment.backOfficeUrl}${serviceUris.lookupCustomerByCode}`;
    const headers = new HttpHeaders({
      dealer: dealerId,
      userLogin: userLogin,
      plantId: plantId || ''
    });
    let params = new HttpParams()
      .set('searchCode', searchCode)
      .set('searchCnpj', String(searchCnpj))
      .set('activeOnly', String(activeOnly))
      .set('customerTypes', customerTypes);
    return this.http.get<any>(url, { headers, params }).pipe(
      map((response) => response?.data ?? null),
      catchError(() => of(null))
    );
  }

  getProductionCustomers(dealerId: string, customerUuid: string): Observable<ProductionCustomer[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getProductionCustomers}`;
    return this.http
      .get<any>(url, {
        headers: { dealer: dealerId },
        params: customerUuid ? { customerUuid: customerUuid } : {}
      })
      .pipe(
        map((response) => {
          const customersData = response.data || [];
          return customersData.map((item: any, index: number) => ({
            id: item.customerInfoSettingsId || item.id,
            code: item.code,
            name: item.customerName,
            label: `${item.code} ${item.customerName}`,
            isActive: item.active || true,
            sortOrder: index + 1
          }));
        }),
        catchError((error) => {
          console.error('Error loading production customers:', error);
          return of(this.getMockProductionCustomersData());
        })
      );
  }

  getSalesmen(dealerId: string): Observable<SalesmanOption[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getSalesmen.replace('{dealerId}', dealerId)}`;
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response?.data || [];
        return data.map((item: any) => ({ id: item.id, login: item.login }));
      }),
      catchError((error) => {
        console.error('Error loading salesmen:', error);
        return of([] as SalesmanOption[]);
      })
    );
  }

  // ===== VALIDATION METHODS =====

  /**
   * Validate work order header data
   */
  validateWorkOrderHeader(): Observable<ValidationResponse> {
    // TODO: Replace with actual API call
    return of({ isValid: true, errors: [] });
    // return this.http.post<ValidationResponse>(`${this.apiUrl}/validate-header`, headerData);
  }

  /**
   * Validate work order line data
   */
  validateWorkOrderLine(): Observable<ValidationResponse> {
    // TODO: Replace with actual API call
    return of({ isValid: true, errors: [] });
    // return this.http.post<ValidationResponse>(`${this.apiUrl}/validate-line`, lineData);
  }

  /**
   * Validate bulk work order request
   */
  validateBulkWorkOrder(): Observable<ValidationResponse> {
    // TODO: Replace with actual API call
    return of({ isValid: true, errors: [] });
    // return this.http.post<ValidationResponse>(`${this.apiUrl}/validate-bulk`, bulkData);
  }

  // ===== SAVE METHODS =====

  /**
   * Save work order header - calls new backend endpoint
   * Replaces legacy WorkOrderHeaderManagedBean.saveWorkOrderHeader()
   * @param request - The work order create request
   * @param dealerId - The dealer ID from component context
   */
  saveWorkOrder(request: CreateWorkOrderRequest, dealerId: string): Observable<CreateWorkOrderResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.createWorkOrder.replace('{dealerId}', dealerId)}`;

    return this.http.post<CreateWorkOrderResponse>(`${url}`, request).pipe(
      catchError((error) => {
        console.error('Save work order failed:', error);
        throw error;
      })
    );
  }

  /**
   * Validate work order number uniqueness (replaces legacy ajaxCheckWoNumValidity)
   * @param workOrderNumber - The work order number to validate
   * @param dealerId - The dealer ID from component context
   */
  validateWorkOrderNumber(workOrderNumber: string, dealerId: string): Observable<{ isUnique: boolean }> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.validateWorkOrderNumber.replace(
      '{dealerId}',
      dealerId
    )}?workOrderNumber=${encodeURIComponent(workOrderNumber)}`;

    return this.http.get<{ data: { unique: boolean } }>(url).pipe(
      map((response) => ({ isUnique: !!response?.data?.unique })),
      catchError((error) => {
        console.error('Work order number validation failed:', error);
        // Return as unique on error to avoid blocking user
        return of({ isUnique: true });
      })
    );
  }

  /**
   * Look up the casing value for CC and CP process codes.
   * Migrated from legacy WorkOrderDetailManagedBean.populateCasingValue():
   *   CC: returns BASE_CREDIT from CASING_CREDIT_DETAIL via customer.casingCreditFk + casingSizeUuid
   *   CP: returns CASING_PRICE from CASING_PURCHASE_DETAIL via customer.casingPurchaseFk + casingSizeUuid
   * Calls backend: GET assets/dealers/{dealerId}/workorders/getCasingValue
   *
   * @param dealerId       - The dealer ID from component context
   * @param processCode    - Line process code (CC or CP); other codes return null
   * @param customerUuid   - Production customer UUID used to resolve credit/purchase FK
   * @param casingSizeUuid - Casing size UUID to look up the detail row
   */
  getCasingValue(
    dealerId: string,
    processCode: string,
    customerUuid: string,
    casingSizeUuid: string
  ): Observable<{ casingValue: number | null }> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getCasingValue.replace('{dealerId}', dealerId)}`;

    return this.http
      .get<{ data: { casingValue: number | null } }>(url, {
        params: { processCode, customerUuid, casingSizeUuid }
      })
      .pipe(
        map((response) => ({ casingValue: response?.data?.casingValue ?? null })),
        catchError((error) => {
          console.error('Get casing value failed:', error);
          return of({ casingValue: null });
        })
      );
  }

  /**
   * Compute the system grade for a work order line.
   * Migrated from legacy PostProcess.updateSystemGrade():
   *   Only BS, DS, CC, CP, RS process codes trigger grade calculation.
   *   Grade is resolved via the customer's CASING_GRADE_TABLE and CasingGradeSpecification rules.
   *   Age is derived from mfgDot (weeks / 4.35 = months, GRA_AGL-1139).
   *   For new lines (no workOrderLinePk) repair counts are all zero.
   * Calls backend: GET assets/dealers/{dealerId}/workorders/getSystemGrade
   *
   * @param dealerId       - The dealer ID from component context
   * @param processCode    - Line process code (BS/DS/CC/CP/RS triggers grade calc)
   * @param customerUuid   - Production customer UUID used to resolve casingGradeTable
   * @param casingSizeUuid - Casing size UUID for size-tier spec matching
   * @param casingMakeFk   - Casing make UUID for tier spec matching (optional)
   * @param mfgDot         - Manufacturing DOT string for age calculation (optional)
   * @param ntr            - Number of Times Retreaded (optional)
   * @param tireAxle       - Axle code for axle spec matching (optional)
   * @param workOrderLinePk - Existing WOL PK for repair count lookup; omit for new lines
   */
  getSystemGrade(
    dealerId: string,
    processCode: string,
    customerUuid: string,
    casingSizeUuid: string,
    casingMakeFk?: string,
    mfgDot?: string,
    ntr?: string,
    tireAxle?: string,
    workOrderLinePk?: string
  ): Observable<{ systemGradeFk: string | null; grade: string | null; description: string | null }> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getSystemGrade.replace('{dealerId}', dealerId)}`;
    const params: Record<string, string> = { processCode, customerUuid, casingSizeUuid };
    if (casingMakeFk) params['casingMakeFk'] = casingMakeFk;
    if (mfgDot) params['mfgDot'] = mfgDot;
    if (ntr) params['ntr'] = ntr;
    if (tireAxle) params['tireAxle'] = tireAxle;
    if (workOrderLinePk) params['workOrderLinePk'] = workOrderLinePk;

    return this.http
      .get<{ data: { systemGradeFk: string | null; grade: string | null; description: string | null } }>(url, { params })
      .pipe(
        map((response) => response?.data ?? { systemGradeFk: null, grade: null, description: null }),
        catchError((error) => {
          console.error('Get system grade failed:', error);
          return of({ systemGradeFk: null, grade: null, description: null });
        })
      );
  }

  /**
   * Get work order lines for table display
   */
  getWorkOrderLines(): Observable<WorkOrderLineInterface[]> {
    // Return empty list by default to avoid displaying dummy/sample lines in the UI.
    // Backend should provide real lines via API when available.
    return of([] as WorkOrderLineInterface[]);
  }

  // ===== PRIVATE MOCK DATA METHODS =====
  // TODO: Remove these when backend APIs are ready

  private getMockDropdownData(): DropdownDataResponse {
    return {
      defaultWorkOrderStatus: '',
      sites: [],
      plants: [],
      processCodes: [],
      makes: [],
      series: [],
      sizes: [],
      treads: []
    };
  }

  private getMockProcessCodesData(): ProcessCode[] {
    return [];
  }

  private getMockTireMakesData(): TireMake[] {
    return [];
  }

  /**
   * Get default work order status from API
   * @returns Observable containing the default work order status
   */
  getDefaultWorkOrderStatus(): Observable<string> {
    // For now, return empty string so UI does not show sample status value.
    // Replace with actual API call when backend is available.
    return of('');
  }

  private getMockTireSpecificationsData(): TireSpecification[] {
    return [];
  }

  private getMockSitesData(): Site[] {
    return [];
  }

  private getMockPlantsData(): Plant[] {
    return [];
  }

  /**
   * Mock production customers based on header customer selection
   */
  private getMockProductionCustomersData(): ProductionCustomer[] {
    return [];
  }

  // ===== BARCODE VALIDATION AND GENERATION =====

  /**
   * Validate barcode uniqueness and business rules
   * Legacy equivalent: WorkOrderLineProcess.isBulkEntryPossible()
   * Calls backend: POST /assets/dealers/{dealerId}/workorders/validateBarcodes
   *
   * @param dealerId - Dealer identifier
   * @param barcodes - List of barcodes to validate
   * @param plantId - Optional plant ID for plant-specific validation
   * @param zone - Zone identifier for DT barcode range validation
   */
  /**
   * Validate barcode uniqueness and business rules
   *
   * @param dealerId The dealer ID for uniqueness check
   * @param barcodes List of barcodes to validate
   * @returns Observable<BarcodeValidationResponse> Validation results
   */
  validateBarcodes(dealerId: string, barcodes: string[]): Observable<BarcodeValidationResponse> {
    const url = `${environment.backOfficeUrl}${serviceUris.validateBarcodes.replace('{dealerId}', dealerId)}`;

    const request: BarcodeValidationRequest = {
      dealerId,
      barcodes
      // Note: plantId and zone removed - no longer used by backend validation
    };

    return this.http.post<any>(url, request).pipe(
      map((response) => response.data as BarcodeValidationResponse),
      catchError((error) => {
        console.error('Error validating barcodes:', error);
        // Return error response
        return of({
          valid: false,
          totalBarcodes: barcodes.length,
          validBarcodes: 0,
          duplicateBarcodes: [],
          oversizedBarcodes: [],
          errorMessages: ['Error validating barcodes: ' + (error.message || 'Unknown error')]
        });
      })
    );
  }

  /**
   * Validate a single work order line barcode.
   * Legacy equivalent: ajaxValidateCasingId() in WorkOrderDetailManagedBean.
   * The server applies fillCasingId() padding (BARCODE_LEADING_ZERO dealer flag) and
   * checks DB uniqueness via verifyCasingIdValidity().
   *
   * @param dealerId - Dealer identifier
   * @param barcode - Raw barcode as typed by the user
   * @returns Observable with normalized barcode and duplicate-existence flag
   */
  validateLineBarcode(dealerId: string, barcode: string): Observable<ValidateLineBarcodeResponse> {
    const url = `${environment.backOfficeUrl}${serviceUris.validateLineBarcode.replace('{dealerId}', dealerId)}`;
    const request: ValidateLineBarcodeRequest = { barcode, dealerId };
    return this.http.post<any>(url, request).pipe(
      map((response) => response.data as ValidateLineBarcodeResponse),
      catchError(() => of({ valid: true, normalizedBarcode: barcode, barcodeExists: false }))
    );
  }

  /**
   * Validate RT DOT (and optional MFG DOT) for a work order line.
   * Mirrors V8 DOT validation in createSingleWorkOrderLineV2 —
   * the same rules are applied at save time, so the user gets immediate feedback.
   *
   * @param dealerId - Dealer identifier (used for zone-based DOT type lookup)
   * @param rtDot    - RT DOT value as entered by the user
   * @param mfgDot   - MFG DOT value — required for the coherence check when both are filled
   * @returns Observable with validity flag and error message
   */
  validateRtDot(dealerId: string, rtDot: string, mfgDot: string): Observable<ValidateRtDotResponse> {
    const url = `${environment.backOfficeUrl}${serviceUris.validateRtDot.replace('{dealerId}', dealerId)}`;
    const request: ValidateRtDotRequest = { dealerId, rtDot, mfgDot };
    return this.http.post<any>(url, request).pipe(
      map((response) => response.data as ValidateRtDotResponse),
      catchError(() => of({ valid: true, errorMessage: null }))
    );
  }

  /**
   * Apply leading zero padding to barcode if BARCODE_LEADING_ZERO flag is enabled
   * Legacy equivalent: fillCasingId() from WorkOrderHeaderManagedBean
   * Pads barcode to 10 digits with leading zeros
   *
   * @param barcode - Original barcode
   * @param applyLeadingZero - Flag from dealer settings
   * @returns Padded barcode or original if flag is false
   */
  applyLeadingZeroPadding(barcode: string, applyLeadingZero: boolean): string {
    if (!barcode || !applyLeadingZero) {
      return barcode;
    }

    // Pad to 10 digits with leading zeros
    return barcode.padStart(10, '0');
  }

  /**
   * Get barcode suffix format for preserving padding
   * Legacy equivalent: getBarcodeSuffixFormat() from WorkOrderLineProcess
   *
   * @param numberSuffix - The numeric suffix extracted from barcode
   * @returns Format string for DecimalFormat (e.g., "000" for 3-digit padding)
   */
  getBarcodeSuffixFormat(numberSuffix: string): string {
    if (!numberSuffix || numberSuffix.length === 0) {
      return '0';
    }

    // Create format string with same number of zeros as original
    return '0'.repeat(numberSuffix.length);
  }

  getMatchingCamSpec(dealerId: string, camRequest: CamRequest): Observable<any> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.matchingCamSpec}`;
    return this.http.post<any>(url, camRequest);
  }

  /**
   * Client-side NTR validation against CAM spec limits.
   * TypeScript port of PostProcess.checkv4NtrCamSpecs() (BTN-13924).
   *
   * Called after getMatchingCamSpec() to validate the NTR value against
   * the matched specification's NTR limit, mirroring
   * WorkOrderDetailManagedBean.checkCamNTR() lines 1426–1430.
   *
   * @param lineValues    - Extracted values from the line work order form.
   *                        Requires: ntrId, workType, ignoreCheckCamSpecNtr, application.
   * @param camSpecResponse - Full response from getMatchingCamSpec().
   *                          Maps to camSpecification.getCamDetails().get(0) in legacy.
   *                          Uses camSpecResponse.camDetails[0].ntr.{limit, action, disposition}.
   * @param m3Flag        - Whether the selected tread is M3 type.
   *                        Derived from wol.getGraTreadDesc().getM3Flag() in legacy;
   *                        not available directly in the form, so passed explicitly.
   * @returns Violation descriptor if the NTR value exceeds the limit, null otherwise.
   */
  checkv4NtrCamSpecs(
    lineValues: { ntrId: any; workType: string; ignoreCheckCamSpecNtr: string; application: ApplicationOption | null },
    camSpecResponse: any,
    m3Flag: boolean
  ): {
    ntrInError: boolean;
    value: number;
    limit: number;
    action?: string;
    disposition?: string;
    disposalCode?: string | null;
    treads?: string[];
    application?: string | null;
    axle?: string | null;
  } | null {
    const camSpecDetails = camSpecResponse?.camDetails?.[0];
    if (!camSpecDetails) {
      return null;
    }

    const ntrSpec = camSpecDetails.ntr;
    if (!ntrSpec || ntrSpec.limit == null) {
      return null;
    }

    // Legacy: if the work order line's application is a yard application, skip NTR check
    const applicationValue: ApplicationOption | null = lineValues.application;
    if (applicationValue?.yardApplication === 'Y') {
      return null;
    }

    const ntrRaw = lineValues.ntrId;
    const ntrParsed = ntrRaw ? parseInt(String(typeof ntrRaw === 'object' ? ntrRaw?.value ?? '' : ntrRaw), 10) : 0;
    const value = isNaN(ntrParsed) ? 0 : ntrParsed;

    const workType: string = lineValues.workType || '';
    // Legacy: GraConstants.YES = 'Y', GraConstants.IGNORED = 'I'.
    // fromWeb is always true in Angular context, so 'I' also suppresses failure.
    const ignoreNtr: string = lineValues.ignoreCheckCamSpecNtr || '';

    let limit = Number(ntrSpec.limit);
    let hasFailure = false;

    if (workType === 'RR') {
      if (m3Flag) {
        // Legacy: M3 tread in RR → NTR must not exceed 1
        limit = 1;
        if (value > limit) {
          hasFailure = true;
        }
      } else if (value >= limit) {
        // Legacy: non-M3 tread in RR → NTR must be strictly less than limit
        hasFailure = true;
      }
    } else {
      // Worktypes RO, IO, SO: NTR must not meet or exceed limit
      if (value >= limit) {
        hasFailure = true;
      }
    }

    // Legacy: suppress failure when ignoreCheckCamSpecNtr is 'Y' (always)
    // or 'I' (fromWeb = true in Angular context)
    if (hasFailure && (ignoreNtr === 'Y' || ignoreNtr === 'I')) {
      hasFailure = false;
    }

    return {
      ntrInError: hasFailure,
      value,
      limit,
      action: ntrSpec.action,
      disposition: ntrSpec.disposition,
      disposalCode: ntrSpec.disposalCode ?? null,
      treads: ntrSpec.treads ?? [],
      application: ntrSpec.application ?? null,
      axle: ntrSpec.axle ?? null
    };
  }

  /**
   * Client-side MDOT (age) validation against CAM spec limits.
   * TypeScript port of PostProcess.checkV4MdotCamSpecs() (BTN-13924).
   *
   * Called after getMatchingCamSpec() to validate the age computed from mfgDot
   * against the matched specification's age limit, mirroring
   * WorkOrderDetailManagedBean.checkCamMDot() lines 1522–1593.
   *
   * Disposal charge handling (legacy: wol.setDisposalChargesFk()) has been moved
   * to the calling method in the component — follow the same pattern as
   * CreateWorkOrderComponent.checkNtrCamViolation.
   *
   * @param lineValues    - Extracted values from the line work order form.
   *                        Requires: mfgDot, application, ignoreCheckCamSpecAge.
   * @param camSpecResponse - Full response from getMatchingCamSpec().
   *                          Maps to camSpecification.getCamDetails().get(0) in legacy.
   *                          Uses camSpecResponse.camDetails[0].age.{limit, action, disposition}.
   * @param settings      - Work order settings providing zone and adjCalMethod for mfgDot parsing.
   * @returns Violation descriptor if the age exceeds the limit, null otherwise.
   */
  checkV4MdotCamSpecs(
    lineValues: { mfgDot: string; application: ApplicationOption | null; ignoreCheckCamSpecAge: string },
    camSpecResponse: any,
    settings: WorkOrderSettings
  ): {
    ageInError: boolean;
    value: number;
    limit: number;
    action?: string;
    disposition?: string;
    disposalCode?: string | null;
    treads?: string[];
    application?: string | null;
    axle?: string | null;
  } | null {
    const camSpecDetails = camSpecResponse?.camDetails?.[0];
    if (!camSpecDetails) {
      return null;
    }

    const ageSpec = camSpecDetails.age;
    if (!ageSpec || ageSpec.limit == null) {
      return null;
    }

    // Legacy: if mfgDot is blank, return null — no age to validate
    const mfgDot: string = lineValues.mfgDot || '';
    if (!mfgDot.trim()) {
      return null;
    }

    // Legacy: if application is a yard application, skip age check
    const applicationValue: ApplicationOption | null = lineValues.application;
    if (applicationValue?.yardApplication === 'Y') {
      return null;
    }

    const ageValue = this.calculateAgeInMonth(mfgDot, settings);
    const limit = Number(ageSpec.limit);

    // Legacy: GraConstants.YES = 'Y', GraConstants.IGNORED = 'I'.
    // fromWeb is always true in Angular context, so 'I' also suppresses failure.
    const ignoreAge: string = lineValues.ignoreCheckCamSpecAge || '';

    let hasFailure = false;
    if (ageValue >= limit) {
      hasFailure = true;
    }

    // Legacy: suppress failure when ignoreCheckCamSpecAge is 'Y' (always)
    // or 'I' (fromWeb = true in Angular context)
    if (hasFailure && (ignoreAge === 'Y' || ignoreAge === 'I')) {
      hasFailure = false;
    }

    return {
      ageInError: hasFailure,
      value: ageValue,
      limit,
      action: ageSpec.action,
      disposition: ageSpec.disposition,
      disposalCode: ageSpec.disposalCode ?? null,
      treads: ageSpec.treads ?? [],
      application: ageSpec.application ?? null,
      axle: ageSpec.axle ?? null
    };
  }

  /**
   * Fetch a single disposal charge by its description for a given dealer.
   * Maps to the new GET /productData/getDisposalChargeByDescription?description={description} endpoint.
   * Used by checkNtrCamViolation to auto-populate lineWOForm.disposalCharge when
   * the CAM spec NTR action is REJECT and disposition is SCRAP.
   *
   * @param dealerId - Dealer identifier (sent as 'dealer' request header)
   * @param description     - Disposal charge CODE value from CamActionDetails.disposition
   * @returns Observable of disposal charge object { id, description, code, ... } or null
   */
  getDisposalChargeByDescription(
    dealerId: string,
    description: string
  ): Observable<{ id: string; code: string; description: string } | null> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getDisposalChargeByDescription}`;
    return this.http.get<any>(url, { params: { description }, headers: { dealer: dealerId } }).pipe(map((res) => res?.data ?? null));
  }

  /**
   * Fetch a single disposal charge by its primary key (DISPOSAL_CHARGES_PK).
   * Legacy equivalent: WorkOrderDetailManagedBean.getDisposalCharge() —
   * when creating a new WOL with SC process code, uses the customer's
   * disposalChargesFk (from CustomerInfoSettings) to resolve the full charge.
   *
   * @param dealerId - Dealer identifier (sent as 'dealer' request header)
   * @param id - The disposal charge PK (customer.disposalChargesFk)
   * @returns Observable of disposal charge object { id, code, description } or null
   */
  getDisposalChargeById(dealerId: string, id: string): Observable<{ id: string; code: string; description: string } | null> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getDisposalChargeById}`;
    return this.http.get<any>(url, { params: { id }, headers: { dealer: dealerId } }).pipe(map((res) => res?.data ?? null));
  }

  /**
   * Validate work order line fields (NTR, MFG DOT, repairs) against the matching CAM spec.
   * Legacy equivalent: PostProcess.checkv4NtrCamSpecs() called from WorkOrderDetailManagedBean.checkCamNTR()
   * Calls backend: POST v1/specifications/validateCams
   *
   * @param dealerId - Required dealer identifier (sent as 'dealer' request header)
   * @param camRequest - CAM validation request including ntr, mdot, workType, etc.
   *   Set ignoreCheckCamSpecNtr=false to enforce NTR check (matching legacy setIgnoreCheckCamSpecNtr(NO))
   */
  validateCamSpec(dealerId: string, camRequest: CamRequest): Observable<any> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.validateCams}`;
    return this.http.post<any>(url, camRequest, { headers: { dealer: dealerId } });
  }

  // ===== MFG DOT VALIDATION =====

  /**
   * Validate a mfgDot value without an API call.
   * Port of GraCalculations.validateMfgDotOnly(String, DotType, ZoneValue, Calendar).
   *
   * DotType is WEEKS (format wwyy) when zone is 'SA' or 'NA', or adjCalMethod is '3'.
   * Otherwise MONTHS (format MMyy).
   * The 2-digit year suffix is expanded to 4 digits via a ±20-year sliding window
   * centred on the current year (port of GraUtils.parseDotDate).
   *
   * @param mfgDot  - 4-digit manufacturing DOT string (e.g. '1226' = week 12 of 2026)
   * @param settings - Work order settings providing zone and adjCalMethod
   * @returns true when the value is blank or valid; false otherwise
   */
  validateMfgDotOnly(mfgDot: string, settings: WorkOrderSettings): boolean {
    if (!mfgDot || !mfgDot.trim()) return true;
    if (mfgDot.length !== 4) return false;
    if (!/^\d{4}$/.test(mfgDot)) return false;

    const useWeeks = settings.zone === 'SA' || settings.zone === 'NA' || settings.adjCalMethod === '3';

    const now = new Date();
    const parsedYear = this.parseDotYear(parseInt(mfgDot.substring(2, 4), 10), now);

    if (useWeeks) {
      // Format wwyy — week of year (01–54)
      const ww = parseInt(mfgDot.substring(0, 2), 10);
      if (ww < 1 || ww > 54) return false;
      const { week: wwNow, year: yyNow } = this.getWeekAndYear(now);
      if (parsedYear > yyNow || (parsedYear === yyNow && ww > wwNow)) return false;
    } else {
      // Format MMyy — calendar month (01–12)
      const mm = parseInt(mfgDot.substring(0, 2), 10);
      if (mm < 1 || mm > 12) return false;
      const mmNow = now.getMonth() + 1; // JS months are 0-based
      const yyNow = now.getFullYear();
      if (parsedYear > yyNow || (parsedYear === yyNow && mm > mmNow)) return false;
    }

    return true;
  }

  /**
   * Calculate the age of a casing in months from its manufacturing DOT string.
   * TypeScript port of GraCalculations.calculateAgeInMonth(String mfgDot).
   *
   * Algorithm (mirrors Java):
   *  1. Parse mfgDot to a Date — WEEKS format (wwyy) for NA/SA zones or adjCalMethod='3',
   *     MONTHS format (MMyy) otherwise.
   *  2. Roll manufacture date back to its preceding Sunday (stays if already Sunday).
   *  3. Roll today forward to the next Sunday (stays if already Sunday).
   *  4. Count month increments until mfgDate reaches or passes today's Sunday.
   *  5. Subtract 1 (mirrors legacy ageInMonth-- at end of loop).
   *
   * @param mfgDot   - 4-digit manufacturing DOT string
   * @param settings - Work order settings providing zone and adjCalMethod
   * @returns Age in whole months (0 if mfgDot is blank or tire is not yet born)
   */
  private calculateAgeInMonth(mfgDot: string, settings: WorkOrderSettings): number {
    if (!mfgDot || !mfgDot.trim()) return 0;

    const useWeeks = settings.zone === 'SA' || settings.zone === 'NA' || settings.adjCalMethod === '3';
    const now = new Date();
    const yy = parseInt(mfgDot.substring(2, 4), 10);
    const expandedYear = this.parseDotYear(yy, now);

    let mfgDate: Date;
    if (useWeeks) {
      // WEEKS format wwyy — first two digits are week-of-year.
      // Compute the Sunday that starts the given week, matching Java's
      // SimpleDateFormat("wwyy") with minimalDaysInFirstWeek=1 and US Sunday start.
      const ww = parseInt(mfgDot.substring(0, 2), 10);
      const jan1DayOfWeek = new Date(expandedYear, 0, 1).getDay(); // 0=Sunday
      const dayOfYearStart = (ww - 1) * 7 - jan1DayOfWeek; // 0-based, may be negative
      mfgDate = new Date(expandedYear, 0, 1 + dayOfYearStart);
    } else {
      // MONTHS format MMyy — first two digits are calendar month.
      const mm = parseInt(mfgDot.substring(0, 2), 10);
      mfgDate = new Date(expandedYear, mm - 1, 1);
    }

    // Roll manufacture date back to the preceding Sunday (or stay if already Sunday)
    while (mfgDate.getDay() !== 0) {
      mfgDate.setDate(mfgDate.getDate() - 1);
    }

    // Roll today forward to the next Sunday (or stay if already Sunday)
    const gc = new Date();
    while (gc.getDay() !== 0) {
      gc.setDate(gc.getDate() + 1);
    }

    // Tire hasn't been manufactured yet relative to today's Sunday
    if (mfgDate > gc) {
      return 0;
    }

    // Count month increments until mfgDate reaches or passes today's Sunday
    const mfgGc = new Date(mfgDate);
    let ageInMonth = 0;
    while (mfgGc < gc) {
      ageInMonth++;
      mfgGc.setMonth(mfgGc.getMonth() + 1);
    }
    return Math.max(0, ageInMonth - 1);
  }

  /**
   * Expands a 2-digit year to a 4-digit year using a sliding window of ±20 years
   * centred on the current year.
   * Port of GraUtils.parseDotDate year-expansion logic.
   */
  private parseDotYear(yy: number, now: Date): number {
    const currentYear4Digit = now.getFullYear();
    const currentYearTwoDigits = currentYear4Digit % 100;
    const limit = (currentYearTwoDigits + 20) % 100;
    const yearFirstPart = currentYear4Digit - currentYearTwoDigits;

    if (limit > currentYearTwoDigits) {
      if (yy <= limit) return yearFirstPart + yy;
      return (Math.floor(yearFirstPart / 100) - 1) * 100 + yy;
    } else {
      if (yy >= currentYearTwoDigits) return yearFirstPart + yy;
      if (yy <= limit) return (Math.floor(yearFirstPart / 100) + 1) * 100 + yy;
      return yearFirstPart + yy;
    }
  }

  /**
   * Returns the week-of-year and year a date belongs to, using US-locale week rules
   * (weeks start on Sunday; week 1 is the week that contains Jan 1).
   * Port of DotDate(Calendar, DotType.WEEKS) + GraUtils.getYearOfWeek.
   *
   * Day-of-year is derived from calendar components (year/month/day) rather than
   * millisecond subtraction, so DST spring-forward transitions — which make one
   * local midnight-to-midnight interval only 23 h — cannot corrupt the count.
   */
  private getWeekAndYear(date: Date): { week: number; year: number } {
    const y = date.getFullYear();
    const m = date.getMonth(); // 0-based
    const d = date.getDate();

    const isLeap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    const daysInMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    let dayOfYear = d - 1; // 0-based, Jan 1 = 0
    for (let i = 0; i < m; i++) dayOfYear += daysInMonth[i];

    // new Date(y, 0, 1).getDay() is a local-calendar-only call — no time component,
    // no DST ambiguity — giving 0 = Sunday, matching Java US-locale Calendar.
    const jan1DayOfWeek = new Date(y, 0, 1).getDay();
    const week = Math.floor((dayOfYear + jan1DayOfWeek) / 7) + 1;

    // Port of GraUtils.getYearOfWeek: late-December dates whose week number rolls
    // into the new year (e.g. Dec 29 reporting week 1) are identified by a small
    // week number (< 10) while still in month 11 (December).
    const year = m === 11 && week < 10 ? y + 1 : y;
    return { week, year };
  }

  /**
   * Compute the next available barcode for the "Save and Add Line" flow.
   * MIGRATED FROM: WorkOrderDetailManagedBean.setBarCodeAfterAddline() +
   *                WorkOrderLineProcess.getNextCasingNumGreaterThan()
   *
   * @param dealerId - Dealer identifier
   * @param barcode  - The current barcode from which to compute the next available value
   * @returns Observable with the computed next barcode
   */
  getNextBarcode(dealerId: string, barcode: string): Observable<NextBarcodeResponse> {
    const url = `${environment.backOfficeUrl}${serviceUris.nextBarcode.replace('{dealerId}', dealerId)}`;
    const request: NextBarcodeRequest = { barcode, dealerId };
    return this.http.post<any>(url, request).pipe(
      map((response) => response.data as NextBarcodeResponse),
      catchError(() =>
        of({
          nextBarcode: null,
          autoIncrementEnabled: false,
          leadingZeroPaddingApplied: false,
          errorMessage: 'Failed to compute next barcode'
        })
      )
    );
  }
}
